import styled from '@emotion/styled'
import { AdminScope } from '@pubstack/common/src/scope'
import { AdminSite } from '@pubstack/common/src/site'
import { FunctionComponent, useEffect, useState } from 'react'
import { FieldValues, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useCreateAdminSite, useDeleteAdminSite, useUpdateAdminSite } from '~/api/admin/admin-site.api'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import Table, { Column, EmptyTable, handleTableSort, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { WithClassName } from '~/types/utils'
import { ADMIN_SCOPE_DETAIL_HEADER_HEIGHT_PX } from '../../PureAdminScopeDetail'
import { AdminScopeSiteSetupFlyout } from '../AdminScopeSiteSetup'

const ScopeSitesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 10px;
`

const SiteActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`

const SiteTableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const ModalInput = styled(Input)`
  margin-top: 4px;
`

const UpdateForm = styled.form`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 34px;
`

type NewSiteModalProps = { scope: AdminScope }
const NewSiteModal: FunctionComponent<NewSiteModalProps> = ({ scope }) => {
  // Hooks
  const modal = useGlobalModal()
  const { control, getValues, reset } = useForm<FieldValues>({
    defaultValues: {
      name: '',
    },
  })

  // Mutations
  const { mutateAsync: createSite, isPending: isCreatingSite } = useCreateAdminSite(scope.id, {
    successToast: (
      <>
        <strong>{getValues('name')}</strong> was successfully created
      </>
    ),
    errorToast: 'Something went wrong when trying to create site',
  })

  const close = () => {
    reset()
    modal.close()
  }

  const handleCreateSite = async () => {
    if (isCreatingSite) return

    const siteName = getValues('name').trim()
    if (!siteName) return

    const newSite: AdminSite = {
      scopeId: scope.id,
      name: siteName,
      pbjsVariableName: scope.pbjs,
      enabled: true,
    } as AdminSite

    await createSite(newSite)

    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>New site</Modal.Title>

      <Modal.Body>
        <ModalInput name={'name'} control={control} label={'Site name'} />
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-admin-scopes-detail-sites-creation-cancel'}>
          Cancel
        </Button>
        <Button
          disabled={isCreatingSite}
          iconName={isCreatingSite ? 'pubstack' : undefined}
          iconSpin={isCreatingSite}
          onClick={handleCreateSite}
          trackingLabel={'common-admin-scopes-detail-sites-creation-confirm'}
        >
          {isCreatingSite ? 'Adding site...' : 'Add site'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

type EditSiteModalProps = { adminSite: AdminSite }
const EditSiteModal: FunctionComponent<EditSiteModalProps> = ({ adminSite }) => {
  // Hooks
  const modal = useGlobalModal()
  const { control, handleSubmit, reset, getValues } = useForm<AdminSite>({
    defaultValues: {
      ...adminSite,
    },
    mode: 'onChange',
  })

  // Mutations
  const { mutateAsync: updateSite, isPending: isUpdatingSite } = useUpdateAdminSite(adminSite.scopeId, adminSite.id, false, {
    successToast: (
      <>
        <strong>{getValues()?.name}</strong> was successfully updated
      </>
    ),
    errorToast: 'Something went wrong when trying to update this site',
  })

  const close = () => {
    reset()
    modal.close()
  }
  const onSubmit: SubmitHandler<AdminSite> = async (site) => {
    if (isUpdatingSite) return

    await updateSite(site)
    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Edit site</Modal.Title>

      <Modal.Body>
        <UpdateForm id={'newScopeForm'} onSubmit={handleSubmit(onSubmit)}>
          <Input name={'name'} control={control} label={'Site name'} />
          <Input name={'pbjsVariableName'} control={control} label={'PBJS Variable'} />
        </UpdateForm>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-admin-scopes-detail-sites-edition-cancel'}>
          Cancel
        </Button>
        <Button
          disabled={isUpdatingSite}
          iconName={isUpdatingSite ? 'pubstack' : undefined}
          iconSpin={isUpdatingSite}
          onClick={handleSubmit(onSubmit)}
          trackingLabel={'common-admin-scopes-detail-sites-edition-confirm'}
        >
          {isUpdatingSite ? 'Editing site...' : 'Edit site'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

type DeleteSiteProps = { site: AdminSite }
const DeleteSiteModal: FunctionComponent<DeleteSiteProps> = ({ site }) => {
  // Hooks
  const { close } = useGlobalModal()

  // Mutations
  const { mutateAsync: deleteSite, isPending: isDeletingSite } = useDeleteAdminSite(site.scopeId, site.id, {
    successToast: (
      <>
        <strong>{site?.name}</strong> was successfully deleted
      </>
    ),
    errorToast: 'Something went wrong when trying to delete this site',
  })

  const handleDeleteSite = async () => {
    if (isDeletingSite) return

    await deleteSite(site)
    close()
  }
  return (
    <Modal.Content>
      <Modal.Title>Remove site</Modal.Title>

      <Modal.Body>
        <p>
          You are about to remove <strong>{site.name}</strong>.
        </p>
        <p>You won’t be able to view its data in Pubstack anymore.</p>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-admin-scopes-detail-sites-deletion-cancel'}>
          Cancel
        </Button>
        <Button
          variant={'negative'}
          iconName={isDeletingSite ? 'pubstack' : 'delete'}
          iconSpin={isDeletingSite}
          disabled={isDeletingSite}
          onClick={handleDeleteSite}
          trackingLabel={'common-admin-scopes-detail-sites-deletion-confirm'}
        >
          {isDeletingSite ? 'Deleting...' : 'Delete'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

const ADMIN_SITE_COLUMNS: (Column | SortableColumn<AdminSite>)[] = [
  {
    name: 'Name',
    isSortable: true,
    order: 'ascending',
    attributeSort: 'name',
  },
  {
    name: 'Site ID',
    isSortable: true,
    order: 'none',
    attributeSort: 'id',
  },
  {
    name: 'Prebid variable',
    isSortable: true,
    order: 'none',
    attributeSort: 'pbjsVariableName',
  },
  {
    name: 'Adm mapping retrieve',
    isSortable: false,
  },
  {
    name: 'Status',
    isSortable: false,
  },
  {
    name: 'Action',
    isSortable: false,
  },
]

type PureAdminScopeSitesProps = WithClassName & {
  scope?: AdminScope
  sites: AdminSite[]
  isLoading: boolean
  onDownload: () => unknown
  onSearch: (search: string) => unknown
}

const UnstyledPureAdminScopeSites: FunctionComponent<PureAdminScopeSitesProps> = ({ className, scope, sites, isLoading, onDownload, onSearch }) => {
  const modal = useGlobalModal()
  const { control } = useForm({
    defaultValues: {
      site: '',
    },
  })
  const siteSearch = useWatch({ control, name: 'site' })

  const [lastSearch, setLastSearch] = useState('')

  useEffect(() => {
    if (lastSearch !== siteSearch) {
      const timeout = setTimeout(() => {
        onSearch(siteSearch)
        setLastSearch(siteSearch)
      }, 400)

      return () => clearTimeout(timeout)
    }
  }, [siteSearch, lastSearch, onSearch])

  const [columns, setColumns] = useState<TableColumns<AdminSite>>(ADMIN_SITE_COLUMNS)

  const displayedSites = handleTableSort(columns, sites)
  const flyout = useGlobalFlyout()

  const openNewSiteModal = () => {
    if (!scope) return
    modal.open(NewSiteModal, { scope })
  }

  const openDeleteSiteModal = (site: AdminSite) => {
    modal.open(DeleteSiteModal, { site })
  }

  const openEditSiteModal = (site: AdminSite) => {
    modal.open(EditSiteModal, { adminSite: site })
  }

  const siteStatus = (site: AdminSite) => {
    if (site.adManagementEnabled) {
      return 'Ad managed'
    } else {
      return 'Analytics'
    }
  }

  return (
    <ScopeSitesWrapper className={className}>
      <SiteActions>
        <SiteTableActions>
          <Input iconLeft={'search'} name={'site'} control={control} label={'Search'} labelIsPlaceholder />
          <Button onClick={openNewSiteModal} trackingLabel={'common-admin-scopes-detail-sites-new'}>
            New site
          </Button>
        </SiteTableActions>
        <Button variant={'secondary'} iconName={'download'} onClick={onDownload} trackingLabel={'common-admin-scopes-detail-sites-download'} />
      </SiteActions>
      <Table
        columns={columns}
        headersTop={ADMIN_SCOPE_DETAIL_HEADER_HEIGHT_PX}
        isLoading={isLoading}
        onClickHeading={(column) => {
          onColumnSort(columns, column, setColumns)
        }}
      >
        {displayedSites.length ? (
          displayedSites.map((site, index) => (
            <TableRow
              key={index}
              clickable={{
                onClick: () =>
                  flyout.open(AdminScopeSiteSetupFlyout, { color: Colors.King, closeTrackingLabel: 'common-admin-scopes-detail-sites-setup-close' }, { scopeId: site.scopeId, siteId: site.id }),
                trackingLabel: 'common-admin-scopes-detail-sites-setup',
              }}
            >
              <TableCell>{site.name}</TableCell>
              <TableCell>{site.id}</TableCell>
              <TableCell>{site.pbjsVariableName}</TableCell>
              <TableCell>
                <Icon name={site.admOnboarding ? 'check' : 'close'} />
              </TableCell>
              <TableCell>{siteStatus(site)}</TableCell>
              <TableCell>
                {/* The row itself opens the setup flyout now, so the buttons have to keep their clicks:
                    without this, Delete would open the flyout on its way to the confirmation. */}
                <div onClick={(event) => event.stopPropagation()}>
                  <Button variant={'tertiary'} iconName={'edit'} onClick={() => openEditSiteModal(site)} trackingLabel={'common-admin-scopes-detail-sites-edit'} />
                  <Button
                    variant="tertiary"
                    iconName="settings"
                    onClick={() =>
                      flyout.open(AdminScopeSiteSetupFlyout, { color: Colors.King, closeTrackingLabel: 'common-admin-scopes-detail-sites-setup-close' }, { scopeId: site.scopeId, siteId: site.id })
                    }
                    trackingLabel="common-admin-scopes-detail-sites-setup"
                  />

                  <Button variant={'tertiary'} iconName={'delete'} onClick={() => openDeleteSiteModal(site)} trackingLabel={'common-admin-scopes-detail-sites-delete'} />
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colspan={columns.length}>
              <EmptyTable>
                <div>
                  <h2>No Sites</h2>
                  <p>There is no existing site for this scope.</p>
                </div>
                <Button onClick={openNewSiteModal} trackingLabel={'common-admin-scopes-detail-sites-new'}>
                  New site
                </Button>
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </ScopeSitesWrapper>
  )
}

export const PureAdminScopeSites = styled(UnstyledPureAdminScopeSites)``
